/**
 * useSaveStay — desa un canvi d'estada: `PATCH /reservations/:id` amb només els camps canviats.
 *
 * El backend ho envia primer a Beds24; si la unitat és ocupada (409) o Beds24 falla (502), no desa
 * res i torna el missatge, que surt en un toast. El fan servir el diàleg «Canviar dates, habitació
 * o preu» de la fitxa i la confirmació d'arrossegar a la graella (runbook 7, F3).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';
import { reservationSaveToast, showResultToast } from '@/lib/beds24-simulated-toast';

/** Cos del `PATCH /reservations/:id`: només el que ha canviat. */
export interface StayPatch {
  data_entrada?: string;
  data_sortida?: string;
  unit_id?: string;
  preu_total?: number;
  /**
   * Bloqueig a l'habitació (`plans/reservation-move-lock.md` §4 F.4): s'ha llegit el motiu i es vol
   * moure igualment. Sense això, moure d'habitació una reserva bloquejada torna 409.
   */
  acknowledge_move_lock?: boolean;
}

export interface SaveStayVariables {
  reservationId: string;
  patch: StayPatch;
}

export interface UseSaveStayOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useSaveStay(options: UseSaveStayOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, patch }: SaveStayVariables) => {
      const res = await api.patch(`/reservations/${reservationId}`, patch);
      return res.data;
    },
    onSuccess: (data, { reservationId }) => {
      // Fora de producció el backend simula l'escriptura a Beds24 (`beds24Simulated`): avís propi.
      showResultToast(reservationSaveToast(data, 'Estada actualitzada'));
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['reservation-full', reservationId] });
      queryClient.invalidateQueries({ queryKey: ['reservation-metadata', reservationId] });
      options.onSuccess?.();
    },
    // 409 (unitat ocupada o reserva bloquejada) i 502 (Beds24 ha fallat): el missatge del backend.
    onError: (error: Error) => {
      toast.error(error.message);
      options.onError?.(error);
    },
  });
}

export default useSaveStay;
